import { useLazyQuery } from '@apollo/client'
import gql from 'graphql-tag'
import {
  Educator,
  Professional,
  Profile,
  ProfileRelatedQueryOutput,
} from 'epc-schema'

/**
 * Profiles are split into multiple types via
 * this Role enum in the GQL schema.
 */
enum Role {
  Educator = 'EDUCATOR',
  Professional = 'PROFESSIONAL',
}

/**
 * Prepopulates the form with related profile data
 * for the current user.
 */
const CURRENT_PROFILE_QUERY = gql`
  query CurrentProfile {
    response: me {
      profile {
        ... on Profile {
          id
          about
          avatarUrl
          bio
          firstName
          lastName
          emailAddress
          phone
          contentAreas
          about
          keywords
          connectionPreference
          gradeLevels
          otherSecondaryLanguages
          secondaryLanguages
          role
        }
        ... on Educator {
          districtId
          districtName
          schoolId
          schoolName
        }
        ... on Professional {
          cities
          preferredActivities
          companyName
          contentAreaJobFields
          jobFieldLowerLevels
          jobField
        }
      }
      errors {
        message
        path
      }
      success
    }
  }
`

export const useCurrentProfile = () => {
  const [getCurrentProfile, { loading, data, error, networkStatus }] =
    useLazyQuery<{
      response: ProfileRelatedQueryOutput
    }>(CURRENT_PROFILE_QUERY, { fetchPolicy: 'network-only' })
  const response = data && data.response
  const currentProfile = (response && response.profile) || ({} as Profile)
  const profileErrors = response ? response.errors : null
  const educator =
    currentProfile.role === Role.Educator ? (currentProfile as Educator) : null
  const professional =
    currentProfile.role === Role.Professional
      ? (currentProfile as Professional)
      : null

  return {
    currentProfile,
    data,
    educator,
    error,
    getCurrentProfile,
    loading,
    networkStatus,
    professional,
    profileErrors,
  }
}
